import chroma from "chroma-js";

export const applyCSSVariables = (color) => {
  const root = document.documentElement;
  if (!color) return;
  try {
    const base = chroma(color);
    const luminance = base.luminance();
    const isLight = luminance > 0.35;

    let sidebarBg, mainBg, cardBg, hoverBg, borderColor, buttonColor, buttonColorLight, textColor, headingText;

    if (isLight) {
      sidebarBg = base.hex();
      mainBg = chroma.mix(color, "#ffffff", 0.65).hex();
      cardBg = "#ffffff";
      hoverBg = base.darken(0.25).hex();
      borderColor = base.darken(0.5).hex();
      buttonColor = base.darken(2.0).saturate(0.5).hex();
      buttonColorLight = base.darken(1.0).saturate(0.3).hex();
      const sidebarLum = chroma(sidebarBg).luminance();
      textColor = sidebarLum > 0.35 ? "#444444" : "#dddddd";
      headingText = "#ffffff";
    } else {
      sidebarBg = base.hex();
      // For very dark colors (near-black), darken() produces no differentiation.
      // Add fixed RGB offsets so each layer stays dark but is clearly distinct.
      if (luminance < 0.04) {
        const [r, g, b] = base.rgb();
        const add = (v, n) => Math.min(255, Math.round(v + n));
        mainBg = chroma(add(r, 17), add(g, 17), add(b, 17)).hex(); // sidebar+17  → ~#111111 for #000
        cardBg = chroma(add(r, 30), add(g, 30), add(b, 30)).hex(); // sidebar+30  → ~#1e1e1e
        hoverBg = chroma(add(r, 40), add(g, 40), add(b, 40)).hex(); // sidebar+40  → ~#282828
        borderColor = chroma(add(r, 55), add(g, 55), add(b, 55)).hex(); // sidebar+55  → ~#373737
        buttonColor = base.brighten(2.0).hex();
        buttonColorLight = base.brighten(2.8).hex();
      } else {
        mainBg = base.darken(0.6).hex();
        cardBg = base.darken(0.3).hex();
        hoverBg = base.brighten(0.25).hex();
        borderColor = base.brighten(0.3).hex();
        buttonColor = base.brighten(1.8).hex();
        buttonColorLight = base.brighten(2.4).hex();
      }
      textColor = "#f0f0f0";
      headingText = "#ffffff";
    }

    const grayText = isLight ? "#777777" : "#c0c0c0";

    // Hyphen variables — used by _global.scss (.main, .collapsedMain backgrounds)
    root.style.setProperty("--body-color", mainBg);
    root.style.setProperty("--sidebar-color", sidebarBg);
    root.style.setProperty("--border-color", borderColor);
    root.style.setProperty("--table-color", cardBg);
    root.style.setProperty("--color-white", "#ffffff");
    root.style.setProperty("--color-black", isLight ? "#000000" : "#ffffff");
    root.style.setProperty("--light-black", isLight ? "#333333" : "#eeeeee");
    root.style.setProperty("--primary-color", mainBg);
    root.style.setProperty("--secondary-color", cardBg);
    root.style.setProperty("--hover-color", hoverBg);
    root.style.setProperty("--primary-text-color", textColor);
    root.style.setProperty("--gray-color1", grayText);
    root.style.setProperty("--blue-color", buttonColor);
    root.style.setProperty("--dynamic-text-color", isLight ? "#333333" : "#ffffff");
    root.style.setProperty("--icon-invert", isLight ? "none" : "brightness(0) invert(1)");
    root.style.setProperty("--button_text_color", "#ffffff");

    // Underscore variables — used by global.css, Sidebar.module.css, Home.module.css, etc.
    root.style.setProperty("--primary_color", mainBg);
    root.style.setProperty("--secondary_color", cardBg);
    root.style.setProperty("--accent_color", sidebarBg);         // sidebar background
    root.style.setProperty("--tertiary_color", hoverBg);         // hover / dropdown bg
    root.style.setProperty("--border_color", borderColor);
    root.style.setProperty("--primary_text_color", textColor);
    root.style.setProperty("--heading_text_color", headingText);
    root.style.setProperty("--button_text_color", "#ffffff");
    root.style.setProperty("--primary_button_color", buttonColor);
    root.style.setProperty("--primary_button_color_light", buttonColorLight);
    root.style.setProperty("--body_color", mainBg);              // used by SmartProfile pages
  } catch (_) { }
};

export const applyDarkTheme = () => {
  const root = document.documentElement;
  // Hyphen variables
  root.style.setProperty("--body-color", "#1e1e1e");
  root.style.setProperty("--sidebar-color", "#111111");
  root.style.setProperty("--border-color", "#333333");
  root.style.setProperty("--table-color", "#222222");
  root.style.setProperty("--color-white", "#000000");
  root.style.setProperty("--color-black", "#ffffff");
  root.style.setProperty("--light-black", "#EEEEEE");
  root.style.setProperty("--primary-color", "#111111");
  root.style.setProperty("--secondary-color", "#222222");
  root.style.setProperty("--hover-color", "#2a2a2a");
  root.style.setProperty("--primary-text-color", "#d0d0d0");
  root.style.setProperty("--gray-color1", "#aaaaaa");
  root.style.setProperty("--blue-color", "#529e83");
  root.style.setProperty("--dynamic-text-color", "#ffffff");
  root.style.setProperty("--icon-invert", "brightness(0) invert(1)");
  // Underscore variables
  root.style.setProperty("--primary_color", "#1e1e1e");
  root.style.setProperty("--secondary_color", "#21293b");
  root.style.setProperty("--accent_color", "#19202e");
  root.style.setProperty("--tertiary_color", "#232c3d");
  root.style.setProperty("--border_color", "#333e56");
  root.style.setProperty("--primary_text_color", "#d0d0d0");
  root.style.setProperty("--heading_text_color", "#ffffff");
  root.style.setProperty("--button_text_color", "#ffffff");
  root.style.setProperty("--primary_button_color", "#529e83");
  root.style.setProperty("--primary_button_color_light", "#76e2c6");
  root.style.setProperty("--body_color", "#1e1e1e");
  root.style.removeProperty("--team-card-color");
};

export const applyLightTheme = () => {
  const root = document.documentElement;
  // Restore original light theme values from _variables.scss defaults
  root.style.setProperty("--body-color", "#fafffd");
  root.style.setProperty("--sidebar-color", "#F2FAFF");
  root.style.setProperty("--border-color", "#DAE9F4");
  root.style.setProperty("--table-color", "#ffffff");
  root.style.setProperty("--color-white", "#ffffff");
  root.style.setProperty("--color-black", "#000000");
  root.style.setProperty("--light-black", "#333333");
  root.style.setProperty("--primary-color", "#ebfff8");
  root.style.setProperty("--secondary-color", "#ffffff");
  root.style.setProperty("--hover-color", "#d4f0e8");
  root.style.setProperty("--primary-text-color", "#333333");
  root.style.setProperty("--gray-color1", "#556376");
  root.style.setProperty("--blue-color", "#529e83");
  root.style.setProperty("--dynamic-text-color", "#333333");
  root.style.setProperty("--icon-invert", "none");
  root.style.setProperty("--button_text_color", "#ffffff");
  // Underscore variables
  root.style.setProperty("--primary_color", "#ebfff8");
  root.style.setProperty("--secondary_color", "#ffffff");
  root.style.setProperty("--accent_color", "#F2FAFF");
  root.style.setProperty("--tertiary_color", "#d4f0e8");
  root.style.setProperty("--border_color", "#d1d5db");
  root.style.setProperty("--primary_text_color", "#333333");
  root.style.setProperty("--heading_text_color", "#000000");
  root.style.setProperty("--button_text_color", "#ffffff");
  root.style.setProperty("--primary_button_color", "#529e83");
  root.style.setProperty("--primary_button_color_light", "#76e2c6");
  root.style.setProperty("--body_color", "#fafffd");
  root.style.removeProperty("--team-card-color");
};

export const applyBrandingColors = (btnColor, crdColor) => {
  const root = document.documentElement;
  if (btnColor) {
    root.style.setProperty("--blue-color", btnColor);
    root.style.setProperty("--primary_button_color", btnColor);
    try {
      const lighter = chroma(btnColor).brighten(0.6).hex();
      root.style.setProperty("--primary_button_color_light", lighter);
    } catch (_) {
      root.style.setProperty("--primary_button_color_light", btnColor);
    }
  }
  if (crdColor) {
    // Card Color from Team Branding is used as card TEXT color, not background
    root.style.setProperty("--team-card-color", crdColor);
  }
};
